import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FiLogIn } from 'react-icons/fi';
import './LoginPage.css';

// Animated morphing logo component
function AnimatedLogo() {
  return (
    <div className="login-logo animated-logo">
      <svg viewBox="0 0 100 100" className="logo-svg">
        {/* Morphing background shapes */}
        <defs>
          <linearGradient id="logoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#2C3E50" />
            <stop offset="100%" stopColor="#34495e" />
          </linearGradient>
        </defs>

        {/* Animated outer ring */}
        <circle
          cx="50" cy="50" r="45"
          fill="none"
          stroke="rgba(255,255,255,0.2)"
          strokeWidth="2"
          className="logo-ring"
        />

        {/* Pulsing inner circle */}
        <circle
          cx="50" cy="50" r="38"
          fill="url(#logoGradient)"
          className="logo-pulse"
        />

        {/* The T letter */}
        <text
          x="50" y="62"
          textAnchor="middle"
          fill="white"
          fontSize="42"
          fontWeight="800"
          fontFamily="Arial Black, sans-serif"
          className="logo-letter"
        >
          T
        </text>

        {/* Orbiting dot */}
        <circle cx="50" cy="8" r="4" fill="rgba(255,255,255,0.6)" className="logo-orbit" />
      </svg>
    </div>
  );
}

function LoginPage() {
  const { login, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Check for error in URL params
  const error = searchParams.get('error');
  const errorDetail = searchParams.get('detail');

  // Redirect if already authenticated — honor a saved deep-link if present
  useEffect(() => {
    if (isAuthenticated && !loading) {
      let returnTo = '/';
      try {
        returnTo = sessionStorage.getItem('returnTo') || '/';
        sessionStorage.removeItem('returnTo');
      } catch (e) { /* sessionStorage unavailable */ }
      navigate(returnTo);
    }
  }, [isAuthenticated, loading, navigate]);

  const getErrorMessage = (errorCode) => {
    switch (errorCode) {
      case 'domain_not_allowed':
        return 'Your email domain is not authorized to access this platform.';
      case 'token_exchange_failed':
        return `Authentication failed${errorDetail ? ` (${errorDetail})` : ''}. Please try again.`;
      case 'userinfo_failed':
        return 'Could not retrieve your account information.';
      case 'no_code':
        return 'Authentication was cancelled.';
      default:
        return 'An error occurred during sign in. Please try again.';
    }
  };

  if (loading) {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-loading">
            <div className="loading-spinner"></div>
            <p>Checking authentication...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-container">
        {/* Logo / Branding */}
        <div className="login-header">
          <AnimatedLogo />
          <h1 className="login-title">Transcend</h1>
          <p className="login-subtitle">Gaming VC Intelligence Platform</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="login-error">
            <span className="error-icon">!</span>
            <p>{getErrorMessage(error)}</p>
          </div>
        )}

        {/* Login Button */}
        <button className="google-login-btn" onClick={login}>
          <svg className="google-icon" viewBox="0 0 24 24" width="20" height="20">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          <span>Sign in with Google</span>
          <FiLogIn className="login-arrow" />
        </button>

        {/* Footer */}
        <div className="login-footer">
          <p>Secure authentication powered by Google OAuth 2.0</p>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
